import { defineStore } from "pinia";
import type { Component } from "vue";
import { markRaw, ref } from "vue";

export type ModalContent = Component | string;

export interface ModalPosition {
  x: number;
  y: number;
}

export interface ModalOptions {
  /**
   * Called when the modal is dismissed via Escape key.
   * Note: close actions triggered by content components (X button, OK/Cancel)
   * are handled by the content component itself.
   */
  onClose?: () => void | Promise<void>;
  /** Allow the modal to be dragged via elements with class "draggable" or "movable". Default: true. */
  draggable?: boolean;
  /** Initial dialog width in pixels. Default: 400. */
  width?: number;
  /** Initial dialog height in pixels. Default: 200. */
  height?: number;
  /** Allow the user to resize the dialog. Default: true. */
  enableResizing?: boolean;
  /** Explicit initial position. Omit (or undefined) to center in the viewport. */
  position?: ModalPosition;
  /** Close the modal when the backdrop mask is clicked. Default: false. */
  closeOnOverlayClick?: boolean;
  /** Close the modal when Escape is pressed. Default: true. */
  closeOnEscapeKeyPressed?: boolean;
  /**
   * Called when the modal is dismissed via overlay click (requires closeOnOverlayClick:true).
   */
  onOverlayClick?: () => void | Promise<void>;
  /**
   * Stable string identifier for this dialog instance.
   * Useful for deduplication (call isOpen(id) before showing) or targeted close.
   * Auto-generated from Date.now() when omitted.
   */
  dialogId?: string;
  /**
   * Props forwarded directly to the content component via v-bind.
   * Only applies when content is a Component (not a string).
   */
  componentProps?: Record<string, unknown>;
}

export interface ModalEntry {
  id: number;
  dialogId: string;
  content: ModalContent;
  onClose?: () => void | Promise<void>;
  draggable: boolean;
  width: number;
  height: number;
  enableResizing: boolean;
  position?: ModalPosition;
  closeOnOverlayClick: boolean;
  closeOnEscapeKeyPressed: boolean;
  onOverlayClick?: () => void | Promise<void>;
  componentProps: Record<string, unknown>;
}

export const useModalStore = defineStore("modal", () => {
  const stack = ref<ModalEntry[]>([]);

  function show(content: ModalContent, options?: ModalOptions): void {
    const dialogId = options?.dialogId ?? String(Date.now());
    // Wrap Component objects in markRaw so Pinia's reactive ref does not make
    // the component definition itself reactive (avoids Vue perf warning).
    const safeContent =
      typeof content === "string" ? content : markRaw(content);
    stack.value.push({
      id: Date.now(),
      dialogId,
      content: safeContent,
      ...(options?.onClose === undefined ? {} : { onClose: options.onClose }),
      draggable: options?.draggable ?? true,
      width: options?.width ?? 400,
      height: options?.height ?? 200,
      enableResizing: options?.enableResizing ?? true,
      ...(options?.position === undefined
        ? {}
        : { position: options.position }),
      closeOnOverlayClick: options?.closeOnOverlayClick ?? false,
      closeOnEscapeKeyPressed: options?.closeOnEscapeKeyPressed ?? true,
      ...(options?.onOverlayClick === undefined
        ? {}
        : { onOverlayClick: options.onOverlayClick }),
      componentProps: options?.componentProps ?? {},
    });
  }

  /** Closes the top-most modal. */
  function close(): void {
    stack.value.pop();
  }

  /** Closes a specific modal by its dialogId (closes topmost if multiple share the id). */
  function closeById(dialogId: string): void {
    const idx = stack.value
      .toReversed()
      .findIndex((m) => m.dialogId === dialogId);
    if (idx === -1) return;
    stack.value.splice(stack.value.length - 1 - idx, 1);
  }

  /** Returns true if a modal with the given dialogId is currently open. */
  function isOpen(dialogId: string): boolean {
    return stack.value.some((m) => m.dialogId === dialogId);
  }

  return { stack, show, close, closeById, isOpen };
});
